import { useCallback, useEffect, useRef } from 'react';

// 20 attempts x 10s = 3 min 20s ceiling. Sized for BE ingest steps whose status is exposed
// inline on the polled response, so each tick can decide whether to keep going; the cap is a
// defensive ceiling against an orphaned step that never reaches a terminal status.
export const BOUNDED_POLL_INTERVAL_MS = 10000;
export const MAX_BOUNDED_POLL_ATTEMPTS = 20;

export interface BoundedPollOptions {
    /** Tick cadence. Default BOUNDED_POLL_INTERVAL_MS. */
    intervalMs?: number;
    /** Attempt ceiling, counting the immediate fire when there is one. Default MAX_BOUNDED_POLL_ATTEMPTS. */
    maxAttempts?: number;
    /** Fire `refetch` as soon as `shouldPoll` turns true, instead of waiting a full interval. Default true. */
    fireImmediately?: boolean;
    /** Observe the backend one last time ON the capping tick, before `onCapExhausted`. Default false. */
    refetchOnCapTick?: boolean;
    /**
     * Scope the attempt budget to a value (e.g. an orgId) instead of to one activation: the budget
     * then survives `shouldPoll` flip-flops and resets only when the key itself changes. Absent →
     * attempts reset on every activation.
     */
    stickyAttemptsKey?: string;
}

/**
 * Bounded interval poll. The caller decides when to start/stop by toggling `shouldPoll`;
 * this hook owns the interval lifecycle, the attempt ceiling, and stable callback handling.
 *
 *   - shouldPoll=true   → fire `refetch(true)` immediately, then every
 *                         BOUNDED_POLL_INTERVAL_MS until MAX_BOUNDED_POLL_ATTEMPTS is hit
 *                         or shouldPoll flips off
 *   - shouldPoll=false  → clear the interval
 *   - unmount           → clear the interval
 *
 * `refetch` receives `true` so callers can distinguish a poll tick from a user-initiated
 * fetch (e.g. to skip the blocking spinner and error toast on background refreshes).
 *
 * `onCapExhausted` fires when the ceiling stops the loop, so a caller that renders an
 * in-flight loader can release it instead of spinning forever on an orphaned step.
 *
 * `isCapReached()` reports whether the budget is spent, for callers that must distinguish a
 * late response arriving after the cap from a live one.
 *
 * The latest callbacks are stashed in refs so the interval always calls the current
 * closures (no stale captures across renders).
 *
 * `options` tunes cadence/ceiling/edge semantics for other pollers (see `useBoundedClassificationPoll`);
 * omitting it gives the defaults described above.
 */
export function useBoundedPoll(
    refetch: (isPollingCall: boolean) => void,
    shouldPoll: boolean,
    onCapExhausted?: () => void,
    options: BoundedPollOptions = {},
) {
    const {
        intervalMs = BOUNDED_POLL_INTERVAL_MS,
        maxAttempts = MAX_BOUNDED_POLL_ATTEMPTS,
        fireImmediately = true,
        refetchOnCapTick = false,
        stickyAttemptsKey,
    } = options;

    const apiPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const attemptsRef = useRef(0);
    const refetchRef = useRef(refetch);
    const onCapExhaustedRef = useRef(onCapExhausted);
    const stickyAttemptsKeyRef = useRef(stickyAttemptsKey);

    useEffect(() => {
        refetchRef.current = refetch;
        onCapExhaustedRef.current = onCapExhausted;
    }, [refetch, onCapExhausted]);

    // Declared before the poll effect so a key change zeroes the budget before the poll restarts.
    useEffect(() => {
        if (stickyAttemptsKeyRef.current !== stickyAttemptsKey) {
            stickyAttemptsKeyRef.current = stickyAttemptsKey;
            attemptsRef.current = 0;
        }
    }, [stickyAttemptsKey]);

    const stopPolling = useCallback(() => {
        if (apiPollingRef.current) {
            clearInterval(apiPollingRef.current);
            apiPollingRef.current = null;
        }
        if (stickyAttemptsKey === undefined) attemptsRef.current = 0;
    }, [stickyAttemptsKey]);

    useEffect(() => {
        if (!shouldPoll) {
            stopPolling();
            return;
        }
        // Only reachable with a sticky budget: a spent budget must not be revived by a re-activation.
        if (attemptsRef.current >= maxAttempts) return;

        if (fireImmediately) refetchRef.current(true);
        if (!apiPollingRef.current) {
            if (stickyAttemptsKey === undefined) attemptsRef.current = 0;
            apiPollingRef.current = setInterval(() => {
                if (refetchOnCapTick) refetchRef.current(true);
                attemptsRef.current += 1;
                if (attemptsRef.current >= maxAttempts) {
                    stopPolling();
                    onCapExhaustedRef.current?.();
                    return;
                }
                if (!refetchOnCapTick) refetchRef.current(true);
            }, intervalMs);
        }
        return () => stopPolling();
    }, [shouldPoll, stopPolling, intervalMs, maxAttempts, fireImmediately, refetchOnCapTick, stickyAttemptsKey]);

    const isCapReached = useCallback(() => attemptsRef.current >= maxAttempts, [maxAttempts]);

    return { isCapReached };
}
